import { type ElementFormatType, type LexicalCommand, type NodeKey, createCommand } from "lexical";
import type { UUID } from "../../../../utils/uuid";
import type { EmailButtonType } from "../../../email-design/types/email-button-type";
import type { PollNodeAnswerType } from "../nodes/poll/types";
import type { CellPadding } from "./cell-padding";
import type { Border } from "./data-table-border";
import type { ButtonNodeBlock } from "./markdown-node-blocks";

export const INSERT_STYLEABLE_TABLE_COMMAND: LexicalCommand<{
  columnProportions?: number[];
}> = createCommand("INSERT_STYLEABLE_TABLE_COMMAND");

export const INSERT_STYLEABLE_TABLE_CELL_COMMAND: LexicalCommand<{
  insertAfter: boolean;
}> = createCommand("INSERT_STYLEABLE_TABLE_CELL_COMMAND");

export const INSERT_DATA_TABLE_COMMAND: LexicalCommand<{
  columns: number;
  rows: number;
  border?: Border;
  cellPadding?: CellPadding;
  backgroundColour?: string;
}> = createCommand("INSERT_DATA_TABLE_COMMAND");

export const INSERT_DATA_TABLE_COLUMN_COMMAND: LexicalCommand<{
  insertAfter: boolean;
}> = createCommand("INSERT_DATA_TABLE_COLUMN_COMMAND");

export const INSERT_DATA_TABLE_ROW_COMMAND: LexicalCommand<{
  position: "above" | "below" | "top" | "bottom";
}> = createCommand("INSERT_DATA_TABLE_ROW_COMMAND");

export const SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND: LexicalCommand<
  | {
      nodeKey: NodeKey;
      individualPadding: true;
      left?: number;
      right?: number;
      top?: number;
      bottom?: number;
    }
  | {
      nodeKey: NodeKey;
      individualPadding: false;
      common?: number;
    }
> = createCommand("SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND");

export const SET_DATA_TABLE_CELL_PADDING_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
  padding: CellPadding;
  skipHistory?: boolean;
}> = createCommand("SET_DATA_TABLE_CELL_PADDING_COMMAND");

export const SET_DATA_TABLE_BACKGROUND_COLOUR_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
  colour: string;
  skipHistory?: boolean;
}> = createCommand("SET_DATA_TABLE_BACKGROUND_COLOUR_COMMAND");

export const SET_TABLE_CELL_BACKGROUND_COLOUR_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
  colour: string;
  skipHistory?: boolean;
}> = createCommand("SET_TABLE_CELL_BACKGROUND_COLOUR_COMMAND");

export const SET_DATA_TABLE_BORDER_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
  border: { width?: number; style?: string; colour?: string };
  skipHistory?: boolean;
}> = createCommand("SET_DATA_TABLE_BORDER_COMMAND");

export const DELETE_IMAGE_COMMAND: LexicalCommand<NodeKey> = createCommand("DELETE_IMAGE_COMMAND");
export const DELETE_NODE_COMMAND: LexicalCommand<NodeKey> = createCommand("DELETE_NODE_COMMAND");

export const RESIZE_START_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
}> = createCommand("RESIZE_START_COMMAND");
export const RESIZE_END_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
  proportionOfMaxWidth: number;
}> = createCommand("RESIZE_END_COMMAND");

export const DESIGN_HUDDLE_EDIT_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
}> = createCommand("DESIGN_HUDDLE_EDIT_COMMAND");

export const DRAGLEAVE_COMMAND = createCommand<DragEvent>("DRAGLEAVE_COMMAND");

export type InsertButtonListCommand = { buttons: ButtonNodeBlock[] };
export const INSERT_BUTTON_LIST_COMMAND: LexicalCommand<InsertButtonListCommand> = createCommand(
  "INSERT_BUTTON_LIST_COMMAND",
);

export type InsertDividerCommand = { type: "primary" | "secondary" } | undefined;
export const INSERT_DIVIDER_COMMAND: LexicalCommand<InsertDividerCommand> =
  createCommand("INSERT_DIVIDER_COMMAND");

export const INSERT_EMOJI_COMMAND: LexicalCommand<{ id: string; native: string }> =
  createCommand("INSERT_EMOJI_COMMAND");

export const INSERT_DYNAMIC_CONTENT_COMMAND: LexicalCommand<{
  segment_id: UUID;
}> = createCommand("INSERT_DYNAMIC_CONTENT_COMMAND");

export const EDIT_DYNAMIC_CONTENT_COMMAND: LexicalCommand<{
  key: NodeKey;
  segment_id: UUID;
}> = createCommand("EDIT_DYNAMIC_CONTENT_COMMAND");

export const CLONE_DYNAMIC_CONTENT_COMMAND: LexicalCommand<NodeKey> = createCommand(
  "CLONE_DYNAMIC_CONTENT_COMMAND",
);

export const SET_DYNAMIC_CONTENT_OPEN_COMMAND: LexicalCommand<{
  key: NodeKey;
  open: boolean;
}> = createCommand("SET_DYNAMIC_CONTENT_OPEN_COMMAND");

export const INSERT_POLL_COMMAND: LexicalCommand<{
  poll_id: UUID;
  answer_type: PollNodeAnswerType;
}> = createCommand("INSERT_POLL_COMMAND");

export const OPEN_EDIT_POLL_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
}> = createCommand("OPEN_EDIT_POLL_COMMAND");

export const MODIFY_POLL_COMMAND: LexicalCommand<{
  nodeKey: NodeKey;
  answer_type: PollNodeAnswerType;
  poll_id: UUID;
}> = createCommand("MODIFY_POLL_COMMAND");

export const INSERT_IMAGE_COMMAND: LexicalCommand<{
  url: string;
  proportionOfMaxWidth: number;
  aspect: number;
  sourceAssetId?: UUID;
}> = createCommand("INSERT_IMAGE_COMMAND");

export const INSERT_IMAGE_FILES_COMMAND: LexicalCommand<{ files: FileList }> = createCommand(
  "INSERT_IMAGE_FILES_COMMAND",
);

export const INSERT_LINK_COMMAND: LexicalCommand<{
  url: string;
  title?: string;
}> = createCommand("INSERT_LINK_COMMAND");

export const INSERT_SURVEY_LINK_COMMAND: LexicalCommand<{
  url: string;
  title?: string;
}> = createCommand("INSERT_SURVEY_LINK_COMMAND");

export const REMOVE_LINK_COMMAND: LexicalCommand<null> = createCommand("REMOVE_LINK_COMMAND");

export const INSERT_MERGE_TAG_COMMAND: LexicalCommand<string> = createCommand(
  "INSERT_MERGE_TAG_COMMAND",
);

export type StylableButtonCommand = {
  text: string;
  url: string;
  buttonType: EmailButtonType;
  fullWidth: boolean;
  format: ElementFormatType;
};
export const INSERT_STYLABLE_BUTTON_COMMAND: LexicalCommand<StylableButtonCommand> = createCommand(
  "INSERT_STYLABLE_BUTTON_COMMAND",
);

export const UPDATE_PAPER_COLOR_COMMAND: LexicalCommand<{ hex?: string; skipHistory?: boolean }> =
  createCommand("UPDATE_PAPER_COLOR_COMMAND");

export const INSERT_VIDEO_COMMAND: LexicalCommand<{
  videoUrl: string;
  thumbnailUrl: string;
  aspect: number;
  proportionOfMaxWidth: number;
  sourceAssetId: UUID;
  title?: string;
}> = createCommand("INSERT_VIDEO_COMMAND");
